import { m, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { X, MapPin, Phone, ArrowUpRight } from "lucide-react";
import { LOCATIONS } from "../data/site";
import { useBookingMode } from "../hooks/useBookingMode";

export const BookingDialog = ({ open, onClose }) => {
  const { direct, bookProps } = useBookingMode();

  return (
  <AnimatePresence>
    {open && (
      <m.div
        data-testid="booking-dialog"
        className="fixed inset-0 z-[80] flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div
          className="absolute inset-0 bg-[#05070b]/88 backdrop-blur-md"
          onClick={onClose}
        />
        <m.div
          initial={{ y: 40, opacity: 0, scale: 0.97 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 20, opacity: 0, scale: 0.98 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="steel-panel grain relative z-10 max-h-[88svh] w-full max-w-xl overflow-y-auto overscroll-contain p-6 sm:p-10"
        >
          <button
            type="button"
            onClick={onClose}
            data-testid="booking-dialog-close"
            aria-label="Close"
            className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center border border-white/10 text-slate-400 transition-colors hover:border-[#c5a059] hover:text-[#d8b46b]"
          >
            <X size={16} />
          </button>

          <span className="label">Reserve your chair</span>
          <h3 className="mt-3 font-cond text-3xl font-extrabold uppercase leading-[0.95] sm:text-4xl">
            Which shop are you
            <span className="brass-text"> heading to?</span>
          </h3>

          <div className="mt-8 space-y-3">
            {LOCATIONS.map((l, i) => (
              <m.div
                key={l.id}
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.12 + i * 0.09, duration: 0.5 }}
                className="group border border-white/[0.08] bg-white/[0.02] p-5 transition-colors duration-300 hover:border-[#c5a059]/45"
              >
                <div className="flex flex-col items-start justify-between gap-4 sm:flex-row">
                  <div>
                    <p className="font-cond text-xl font-bold uppercase tracking-wide text-[#f3ece0]">
                      {l.city}
                    </p>
                    <p className="mt-1.5 flex items-start gap-2 text-xs text-slate-400">
                      <MapPin size={13} className="mt-0.5 shrink-0 text-[#c5a059]" />
                      {l.address}
                    </p>
                    <a
                      href={`tel:${l.tel}`}
                      className="mt-1 flex items-center gap-2 text-xs text-slate-400 hover:text-[#d8b46b]"
                    >
                      <Phone size={13} className="text-[#c5a059]" />
                      {l.phone}
                    </a>
                  </div>
                  <a
                    {...bookProps(l)}
                    data-testid={`dialog-book-${l.id}`}
                    className="flex min-h-[48px] w-full shrink-0 items-center justify-center bg-[#c5a059] px-5 font-cond text-[12px] font-bold uppercase tracking-[0.18em] text-[#0a0d12] transition-all duration-300 group-hover:bg-[#d8b46b] sm:w-auto"
                  >
                    Book <ArrowUpRight size={12} className="ml-1 inline" />
                  </a>
                </div>
              </m.div>
            ))}
          </div>

          <p className="mt-6 text-[11px] uppercase tracking-[0.2em] text-slate-500">
            Walk-ins welcome · Open every day 8AM to 7PM
          </p>

          {direct ? (
            <p
              data-testid="dialog-direct-note"
              className="mt-4 border border-[#c5a059]/25 bg-[#c5a059]/[0.07] p-4 text-[11px] leading-relaxed uppercase tracking-[0.14em] text-[#e2cf9e]"
            >
              Booking direct with the shop · your chair is confirmed by our front
              desk
            </p>
          ) : (
            <Link
              to="/book"
              data-testid="dialog-book-direct"
              className="mt-4 inline-flex items-center gap-2 border-b border-[#c5a059]/40 pb-0.5 text-[11px] uppercase tracking-[0.18em] text-slate-400 transition-colors hover:border-[#c5a059] hover:text-[#d8b46b]"
            >
              Booking page not loading? Reserve here instead
              <ArrowUpRight size={11} />
            </Link>
          )}
        </m.div>
      </m.div>
    )}
  </AnimatePresence>
  );
};
